import { X } from "lucide-react";
import Modalant from "../../../../../../components/ModalA/Modalant";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./UI/select";
import {
  FilterCriteria,
  IncidentQueryField,
  FIELD_OPERATORS,
} from "../types/salesforce";
import { FilterInput } from "./FilterSection";

interface FilterSectionModalProps {
  showAddFilter: boolean;
  setShowAddFilter: (show: boolean) => void;
  localFilters: (FilterCriteria & { id: string })[];
  availableFields: IncidentQueryField[];
  availableGroupFields: IncidentQueryField[];
  picklistValues: Record<string, string[]>;
  handleFieldChange: (id: string, value: string) => void;
  updateFilter: (id: string, updates: Partial<FilterCriteria>) => void;
  removeFilter: (id: string) => void;
  addPicklistValue: (id: string, value: string) => void;
  removePicklistValue: (id: string, value: string) => void;
}

const isPicklistField = (field: IncidentQueryField): boolean => {
  return (
    field.Field_Type__c === "Picklist" ||
    field.Field_Type__c === "MultiselectPicklist" ||
    field.Treat_as_Picklist__c === true
  );
};

const FilterSectionModal = ({
  showAddFilter,
  setShowAddFilter,
  localFilters,
  availableFields,
  availableGroupFields,
  picklistValues,
  handleFieldChange,
  updateFilter,
  removeFilter,
  addPicklistValue,
  removePicklistValue,
}: FilterSectionModalProps) => {
  const TEXT_OPERATORS = [
    "LIKE",
    "NOT LIKE",
    "STARTS WITH",
    "ENDS WITH",
    "CONTAINS",
    "IN",
  ];

  const handleClose = () => {
    const hasEmptyField = localFilters.some(
      (filter) =>
        !filter.fieldName ||
        (TEXT_OPERATORS.includes(filter.operator) &&
          (!filter.value || filter.value.length === 0))
    );

    if (hasEmptyField) {
      localFilters.forEach((filter) => {
        if (
          !filter.fieldName ||
          (TEXT_OPERATORS.includes(filter.operator) &&
            (!filter.value || filter.value.length === 0))
        ) {
          removeFilter(filter.id);
        }
      });
    }

    setShowAddFilter(false);
  };

  return (
    <Modalant isOpen={showAddFilter} isClose={handleClose}>
      <div className="space-y-4">
        {localFilters.map((filter, index) => {
          const selectedField = availableFields.find(
            (f) => f.Field_API_Name__c === filter.fieldName
          );

          return (
            <div
              key={filter.id}
              className="flex flex-col gap-2 rounded bg-white-main p-3"
            >
              <div className="flex items-center gap-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-white-main">
                  {index + 1}
                </div>

                <Select
                  value={filter.fieldName}
                  onValueChange={(value) => handleFieldChange(filter.id, value)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select field" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">None</SelectItem>
                    {availableGroupFields.map((field) => (
                      <SelectItem
                        key={field.Field_API_Name__c}
                        value={field.Field_API_Name__c}
                      >
                        {field.Label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {filter.fieldName &&
                  selectedField &&
                  !isPicklistField(selectedField) && (
                    <Select
                      value={filter.operator}
                      onValueChange={(value) =>
                        updateFilter(filter.id, { operator: value })
                      }
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue placeholder="Select operator" />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_OPERATORS[
                          selectedField.Field_Type__c as keyof typeof FIELD_OPERATORS
                        ]?.map((op) => (
                          <SelectItem key={op} value={op}>
                            {op}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}

                {filter.fieldName && selectedField && (
                  <div className="">
                    <FilterInput
                      filter={filter}
                      selectedField={selectedField}
                      picklistValues={picklistValues}
                      onUpdate={(updates) => updateFilter(filter.id, updates)}
                      onPicklistValueAdd={(value) =>
                        addPicklistValue(filter.id, value)
                      }
                      onPicklistValueRemove={(value) =>
                        removePicklistValue(filter.id, value)
                      }
                    />
                  </div>
                )}

                <button
                  onClick={() => removeFilter(filter.id)}
                  className="focus-visible:ring-ring ring-offset-background hover:text-accent-foreground inline-flex h-9 cursor-pointer items-center justify-center rounded-md border-0 px-3 text-sm font-medium text-red-500 transition-colors hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {filter.value &&
                Array.isArray(filter.value) &&
                filter.value.length > 0 && (
                  <div className="flex flex-wrap gap-2 pl-12">
                    {filter.value.map((value) => (
                      <div
                        key={value}
                        className="flex items-center gap-1 rounded bg-gray-100 px-2 py-1"
                      >
                        <span className="text-sm">{value}</span>
                        <button
                          onClick={() => removePicklistValue(filter.id, value)}
                          className="cursor-pointer border-0 text-red-500 hover:bg-red-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          );
        })}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleClose}
            disabled={availableGroupFields.length === 0}
            className="w-full cursor-pointer rounded border-0 bg-purple-400 p-1 text-white-main hover:bg-purple-500 md:w-auto"
          >
            Add
          </button>
        </div>
      </div>
    </Modalant>
  );
};

export default FilterSectionModal;
